/** Használati statisztika — események küldése (rendszerterv 9.1; tájékoztató 4.16).
 *
 *  A `track()` sorba teszi az eseményt, és pár másodpercenként (vagy amikor a lap a háttérbe
 *  kerül) egy kötegben, `navigator.sendBeacon`-nel küldi a `POST /api/v1/usage/events`-re.
 *  Sütit és helyi tárolót nem használ. Amíg a szerver nem mondta meg, hogy rögzít-e
 *  (`/usage/status`), a köteg vár; ha nem rögzít (nincs elindítva, vagy a felhasználó a
 *  profilban kikapcsolta), a sor kiürül, és új esemény be sem kerül. Az események nevét és
 *  mezőit a szerver regisztere szűri (`app/analytics/events.py`). */
import { isFramed } from "./view-mode";

export interface TrackEvent {
  event: string;
  object_id?: string;
  props?: Record<string, string | number | boolean>;
  frame?: boolean;
}

const ENDPOINT = "/api/v1/usage/events";
const FLUSH_MS = 4000;
const MAX_BATCH = 50;
const MAX_QUEUE = 200;

let active: boolean | null = null;
let queue: TrackEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function send(events: TrackEvent[]): void {
  const body = JSON.stringify({ events });
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }))) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }).catch(() => undefined);
  } catch {
    // a statisztika soha nem zavarhatja a használatot
  }
}

export function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (active !== true || queue.length === 0) return;
  while (queue.length > 0) send(queue.splice(0, MAX_BATCH));
}

function schedule(): void {
  if (timer || active !== true) return;
  timer = setTimeout(flush, FLUSH_MS);
}

/** A szerver válasza a `/usage/status`-ra: rögzít-e most. */
export function setTrackingActive(value: boolean): void {
  active = value;
  if (!value) {
    queue = [];
    if (timer) clearTimeout(timer);
    timer = null;
  } else {
    schedule();
  }
}

export function track(
  event: string,
  objectId?: string | number | null,
  props?: TrackEvent["props"],
): void {
  if (active === false || queue.length >= MAX_QUEUE) return;
  queue.push({
    event,
    ...(objectId !== undefined && objectId !== null ? { object_id: String(objectId) } : {}),
    ...(props ? { props } : {}),
    ...(isFramed() ? { frame: true } : {}),
  });
  if (queue.length >= MAX_BATCH) flush();
  else schedule();
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}

/** Csak tesztekhez: alapállapot. */
export function resetTracking(): void {
  active = null;
  queue = [];
  if (timer) clearTimeout(timer);
  timer = null;
}
